/**
 * CommissionTableRow Component
 * 수수료 테이블 행 컴포넌트
 * - 현재 적용 중인 수수료 표시
 * - 수수료변경 버튼 클릭 시 새 수수료 입력 행 표시
 */
import React from 'react';
import DateInput from '@/shared/components/forms/DateInput';
import { formatCurrency } from '@/shared/utils/formatter';
import { SupplierCommissionRow, PeriodType } from '../types/feeManagement.types';
import { COMMISSION_TYPE_OPTIONS, getAmountUnit, getTodayString } from '../utils/feeManagementHelpers';

interface CommissionTableRowProps {
    row: SupplierCommissionRow;
    index: number;
    onNewCommissionTypeChange: (supplierId: string, period: PeriodType, value: string) => void;
    onNewAmountChange: (supplierId: string, period: PeriodType, value: string) => void;
    onNewPaybackUseYnChange: (supplierId: string, value: string) => void;
    onNewPaybackAmountChange: (supplierId: string, value: string) => void;
    onNewStartDateChange: (supplierId: string, value: string) => void;
    onShowHistory: (supplierId: string, supplierName: string) => void;
    onSave: (supplierId: string) => void;
    onToggleFeeChange: (supplierId: string) => void;
}

/** 수수료 구분 코드를 한글 레이블로 변환 */
const getCommissionTypeLabel = (type: string): string => {
    const option = COMMISSION_TYPE_OPTIONS.find(opt => opt.value === type);
    return option ? option.label : '-';
};

/** 금액 포맷팅 (단위 포함) */
const formatAmountWithUnit = (amount: string, type: string): string => {
    if (!amount || !type) return '-';
    const unit = getAmountUnit(type);
    return `${formatCurrency(amount)}${unit}`;
};

const CommissionTableRow: React.FC<CommissionTableRowProps> = ({
                                                                   row,
                                                                   index,
                                                                   onNewCommissionTypeChange,
                                                                   onNewAmountChange,
                                                                   onNewPaybackUseYnChange,
                                                                   onNewPaybackAmountChange,
                                                                   onNewStartDateChange,
                                                                   onShowHistory,
                                                                   onSave,
                                                                   onToggleFeeChange
                                                               }) => {
    const handleNewAmountInput = (period: PeriodType, value: string) => {
        const numericValue = value.replace(/[^0-9]/g, '');
        const commissionType = period === 'daily' ? row.newDailyCommissionType : row.newWeeklyCommissionType;
        if (commissionType?.includes('RATE')) {
            const numValue = parseInt(numericValue) || 0;
            if (numValue > 100) return;
        }
        onNewAmountChange(row.supplierId, period, numericValue);
    };

    const showValidation = row.isEdited;
    const dailyAmountError = showValidation && row.newDailyCommissionType && !row.newDailyAmount ? '금액을 입력하세요' : '';
    const weeklyAmountError = showValidation && row.newWeeklyCommissionType && !row.newWeeklyAmount ? '금액을 입력하세요' : '';
    const paybackAmountError = showValidation && row.newWeeklyPaybackUseYn === 'Y' && !row.newWeeklyPaybackAmount ? '금액을 입력하세요' : '';
    const startDateError = showValidation && !row.newStartDate ? '적용일을 선택하세요' : '';
    const hasValidationError = !!(dailyAmountError || weeklyAmountError || paybackAmountError || startDateError);

    const errorInputStyle: React.CSSProperties = {
        border: '1px solid #ef4444',
        outline: 'none',
        boxShadow: '0 0 0 2px rgba(239,68,68,0.15)',
    };
    const errorTextStyle: React.CSSProperties = {
        fontSize: '0.65rem', color: '#ef4444', marginTop: '2px',
    };

    return (
        <>
            {/* 현재 적용 중인 수수료 행 */}
            <tr style={row.isAddingNew ? { backgroundColor: 'rgba(5, 205, 130, 0.04)' } : undefined}>
                {/* NO */}
                <td
                    className="text-center"
                    rowSpan={row.isAddingNew ? 2 : 1}
                    style={row.isAddingNew ? { borderLeft: '3px solid var(--vroong-primary)' } : undefined}
                >
                    {index}
                </td>

                {/* 협력사명 */}
                <td rowSpan={row.isAddingNew ? 2 : 1}>
                    <span>{row.supplierName}</span>
                </td>

                {/* 선정산 */}
                <td className="text-center">
                    {row.dailyCommissionType ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                            <div style={{ fontSize: '0.8rem', color: '#4b5563' }}>
                                {getCommissionTypeLabel(row.dailyCommissionType)}
                            </div>
                            <div style={{ fontSize: '0.9rem', fontWeight: '600' }}>
                                {formatAmountWithUnit(row.dailyAmount, row.dailyCommissionType)}
                            </div>
                        </div>
                    ) : (
                        <span>-</span>
                    )}
                </td>

                {/* 주정산 */}
                <td className="text-center">
                    {row.weeklyCommissionType ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                            <div style={{ fontSize: '0.8rem', color: '#4b5563' }}>
                                {getCommissionTypeLabel(row.weeklyCommissionType)}
                            </div>
                            <div style={{ fontSize: '0.9rem', fontWeight: '600' }}>
                                {formatAmountWithUnit(row.weeklyAmount, row.weeklyCommissionType)}
                            </div>
                        </div>
                    ) : (
                        <span>-</span>
                    )}
                </td>

                {/* 페이백(건당) */}
                <td className="text-center">
                    {row.weeklyPaybackUseYn === 'Y' && row.weeklyPaybackAmount ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                            <span style={{
                                display: 'inline-flex', alignItems: 'center', gap: '3px',
                                fontSize: '0.7rem', fontWeight: 'bold', color: '#fff',
                                backgroundColor: '#059669', padding: '2px 7px', borderRadius: '10px',
                            }}>
                                <i className="fas fa-check-circle"></i> 사용중
                            </span>
                            <div style={{ fontSize: '0.9rem', fontWeight: '600' }}>
                                {formatCurrency(row.weeklyPaybackAmount)}원
                            </div>
                        </div>
                    ) : row.scheduledPaybackUseYn === 'Y' && row.scheduledPaybackAmount ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                            <span style={{
                                display: 'inline-flex', alignItems: 'center', gap: '3px',
                                fontSize: '0.7rem', fontWeight: 'bold', color: '#fff',
                                backgroundColor: '#2563eb', padding: '2px 7px', borderRadius: '10px',
                            }}>
                                <i className="fas fa-clock"></i> 적용 예정
                            </span>
                            <div style={{ fontSize: '0.75rem', color: '#2563eb' }}>
                                {row.scheduledPaybackApplyDate}
                            </div>
                            <div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#2563eb' }}>
                                {formatCurrency(row.scheduledPaybackAmount)}원
                            </div>
                        </div>
                    ) : (
                        <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: '3px',
                            fontSize: '0.7rem', fontWeight: 'bold', color: '#6b7280',
                            backgroundColor: '#f3f4f6', padding: '2px 7px', borderRadius: '10px',
                        }}>
                            <i className="fas fa-minus-circle"></i> 미사용
                        </span>
                    )}
                </td>

                {/* 적용일 */}
                <td className="text-center">
                    {row.startDate || '-'}
                </td>

                {/* 관리 (이력 + 변경/취소+저장) */}
                <td className="text-center" rowSpan={row.isAddingNew ? 2 : 1}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
                        <button
                            className="btn btn-secondary"
                            style={{ fontSize: '0.8rem', padding: '4px 8px', width: '100%' }}
                            onClick={() => onShowHistory(row.supplierId, row.supplierName)}
                        >
                            <i className="fas fa-history"></i>
                            <span style={{ marginLeft: '3px' }}>이력</span>
                        </button>
                        {row.isAddingNew ? (
                            <div style={{ display: 'flex', gap: '4px', width: '100%' }}>
                                <button
                                    className="btn btn-secondary"
                                    style={{ fontSize: '0.8rem', padding: '4px 8px', flex: 1 }}
                                    onClick={() => onToggleFeeChange(row.supplierId)}
                                >
                                    <i className="fas fa-times"></i>
                                    <span style={{ marginLeft: '3px' }}>취소</span>
                                </button>
                                <button
                                    className="btn btn-primary"
                                    style={{ fontSize: '0.8rem', padding: '4px 8px', flex: 1 }}
                                    onClick={() => onSave(row.supplierId)}
                                    disabled={hasValidationError}
                                >
                                    <i className="fas fa-save"></i>
                                    <span style={{ marginLeft: '3px' }}>저장</span>
                                </button>
                            </div>
                        ) : (
                            <button
                                className="btn btn-primary"
                                style={{ fontSize: '0.8rem', padding: '4px 8px', width: '100%' }}
                                onClick={() => onToggleFeeChange(row.supplierId)}
                            >
                                <i className="fas fa-edit"></i>
                                <span style={{ marginLeft: '3px' }}>변경</span>
                            </button>
                        )}
                    </div>
                </td>
            </tr>

            {/* 새 수수료 입력 행 (수수료변경 클릭 시 표시) */}
            {row.isAddingNew && (
                <tr style={{ backgroundColor: 'rgba(5, 205, 130, 0.08)' }}>
                    {/* 선정산 입력 */}
                    <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', padding: '4px' }}>
                            <div style={{ fontSize: '0.7rem', color: 'var(--vroong-primary)', fontWeight: 'bold' }}>
                                변경할 수수료
                            </div>
                            <select
                                className="filter-select"
                                style={{ width: '100%', fontSize: '0.8rem' }}
                                value={row.newDailyCommissionType || ''}
                                onChange={(e) => onNewCommissionTypeChange(row.supplierId, 'daily', e.target.value)}
                            >
                                {COMMISSION_TYPE_OPTIONS.map(option => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                                <input
                                    type="text"
                                    className="filter-input"
                                    style={{ textAlign: 'right', fontSize: '0.85rem', ...(dailyAmountError ? errorInputStyle : {}) }}
                                    value={row.newDailyAmount ? formatCurrency(row.newDailyAmount) : ''}
                                    onChange={(e) => handleNewAmountInput('daily', e.target.value)}
                                    placeholder="금액"
                                    disabled={!row.newDailyCommissionType}
                                />
                                <span style={{ fontSize: '0.85rem' }}>
                                    {row.newDailyCommissionType ? getAmountUnit(row.newDailyCommissionType) : ''}
                                </span>
                            </div>
                            {dailyAmountError && <div style={errorTextStyle}>{dailyAmountError}</div>}
                        </div>
                    </td>

                    {/* 주정산 입력 */}
                    <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', padding: '4px' }}>
                            <div style={{ fontSize: '0.7rem', color: 'var(--vroong-primary)', fontWeight: 'bold' }}>
                                변경할 수수료
                            </div>
                            <select
                                className="filter-select"
                                style={{ width: '100%', fontSize: '0.8rem' }}
                                value={row.newWeeklyCommissionType || ''}
                                onChange={(e) => onNewCommissionTypeChange(row.supplierId, 'weekly', e.target.value)}
                            >
                                {COMMISSION_TYPE_OPTIONS.map(option => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                                <input
                                    type="text"
                                    className="filter-input"
                                    style={{ textAlign: 'right', fontSize: '0.85rem', ...(weeklyAmountError ? errorInputStyle : {}) }}
                                    value={row.newWeeklyAmount ? formatCurrency(row.newWeeklyAmount) : ''}
                                    onChange={(e) => handleNewAmountInput('weekly', e.target.value)}
                                    placeholder="금액"
                                    disabled={!row.newWeeklyCommissionType}
                                />
                                <span style={{ fontSize: '0.85rem' }}>
                                    {row.newWeeklyCommissionType ? getAmountUnit(row.newWeeklyCommissionType) : ''}
                                </span>
                            </div>
                            {weeklyAmountError && <div style={errorTextStyle}>{weeklyAmountError}</div>}
                        </div>
                    </td>

                    {/* 페이백 입력 */}
                    <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', padding: '4px' }}>
                            <div style={{ fontSize: '0.7rem', color: 'var(--vroong-primary)', fontWeight: 'bold' }}>
                                페이백 설정
                            </div>
                            <select
                                className="filter-select"
                                style={{ width: '100%', fontSize: '0.8rem' }}
                                value={row.newWeeklyPaybackUseYn || 'N'}
                                onChange={(e) => onNewPaybackUseYnChange(row.supplierId, e.target.value)}
                            >
                                <option value="N">미사용</option>
                                <option value="Y">사용</option>
                            </select>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                                <input
                                    type="text"
                                    className="filter-input"
                                    style={{ textAlign: 'right', fontSize: '0.85rem', ...(paybackAmountError ? errorInputStyle : {}) }}
                                    value={row.newWeeklyPaybackAmount ? formatCurrency(row.newWeeklyPaybackAmount) : ''}
                                    onChange={(e) => {
                                        const numericValue = e.target.value.replace(/[^0-9]/g, '');
                                        onNewPaybackAmountChange(row.supplierId, numericValue);
                                    }}
                                    placeholder="금액"
                                    disabled={row.newWeeklyPaybackUseYn !== 'Y'}
                                />
                            </div>
                            {paybackAmountError && <div style={errorTextStyle}>{paybackAmountError}</div>}
                        </div>
                    </td>

                    {/* 적용일 입력 */}
                    <td className="text-center">
                        <div style={{ padding: '4px' }}>
                            <div style={startDateError ? { borderRadius: '4px', ...errorInputStyle, padding: '1px' } : undefined}>
                                <DateInput
                                    id={`newStartDate-${row.supplierId}`}
                                    value={row.newStartDate || ''}
                                    onChange={(value) => onNewStartDateChange(row.supplierId, value)}
                                    min={getTodayString()}
                                    className="form-input"
                                />
                            </div>
                            {startDateError && <div style={errorTextStyle}>{startDateError}</div>}
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
};

export default CommissionTableRow;
